import type * as Y from 'yjs'
import { XmlText } from 'yjs'

/**
 * One operation of a document `content` Y.XmlText delta. Every block is a
 * nested Y.XmlText whose element properties are stored as its attributes,
 * e.g. `{ id, type: 'core/text', class: 'text', properties: { role: 'heading-1' } }`.
 */
interface BlockAttributes {
  properties?: {
    role?: string
  }
}

/**
 * Whether a status change applies the title fallback.
 *
 * Every status change flushes the document through `fromYjsNewsDoc`, whose
 * `makeTitle` stores `root.title` as the document title when the headline is
 * empty - so these are the transitions where the headline should be filled in to
 * match, and the ones the publish prompt warns about. Keep the warning and the
 * write on this one predicate so they cannot drift apart.
 *
 * `draft` is excluded because an empty headline on a draft is expected, and
 * `unpublished` because it is a retraction (it also never reaches the status
 * change callback, going through `StatusMenu`'s `unPublishDocument` instead).
 */
export function appliesTitleFallback(status: string): boolean {
  return status !== 'draft' && status !== 'unpublished'
}

/**
 * The document types the title fallback is applied to.
 *
 * These are the types the Editor view serves whose templates ship an empty
 * heading-1 and a `title` taken from the assignment, so they are the types
 * `EditorHeader` fills. `core/flash` is a text document to `makeTitle` as well,
 * but it is served by `QuickDocument/DocumentHeader`, which neither runs the
 * fill nor renders the status prompts, so it is deliberately left out.
 */
const TITLE_FALLBACK_TYPES = ['core/article', 'core/article#timeless', 'core/editorial-info']

/**
 * Whether a document type applies the title fallback. Pairs with
 * {@link appliesTitleFallback}, which answers the same question for a status.
 */
export function hasTitleFallback(documentType: string | undefined): boolean {
  return !!documentType && TITLE_FALLBACK_TYPES.includes(documentType)
}

/**
 * Write `title` into the article's heading-1 block when that block is empty,
 * applying the title fallback to the content instead of leaving it to
 * `makeTitle` in `shared/transformations/yjsNewsDoc.ts`, which publishes
 * `heading-1 || root.title` but only for the document title - the published
 * content keeps its empty headline.
 *
 * Mirrors `makeTitle`'s condition exactly: the fallback only kicks in for a raw
 * falsy heading, so a whitespace-only heading is published as-is and must not be
 * overwritten here. Never trim, or this will disagree with what is published.
 *
 * Writing straight to the Y.XmlText puts the title in the local Y.Doc state that
 * `snapshotDocument` sends on to the repository, and gives the change an origin
 * other than Textbit's `localOrigin`. `withYHistory` only tracks `localOrigin`
 * transactions, so the written title stays out of the user's undo stack.
 *
 * @returns true when the title was written, false when nothing was changed.
 */
export function applyArticleTitleFallback(content: Y.XmlText, title: string | undefined): boolean {
  if (!title) {
    return false
  }

  const heading = findHeadingBlock(content)
  if (!heading || heading.length) {
    return false
  }

  // A single insert is atomic, so no explicit transaction is needed.
  heading.insert(0, title)

  return true
}

/**
 * Find the nested Y.XmlText holding the first heading-1 block, if any.
 */
function findHeadingBlock(content: Y.XmlText): Y.XmlText | undefined {
  const delta = content.toDelta() as { insert?: unknown }[]

  for (const { insert } of delta) {
    if (insert instanceof XmlText) {
      const attributes = insert.getAttributes() as BlockAttributes

      if (attributes?.properties?.role === 'heading-1') {
        return insert
      }
    }
  }

  return undefined
}
